import type { Coordinate } from "@/entities/tracking";
import { env } from "@/shared/constants/env";
import type { AddressSuggestion } from "./geocoding";
import { loadGoogleMapsLibrary } from "./googleMapsLoader";

// Recherche d'adresses via Google Places (Autocomplete + Place Details).
//
// ## Pourquoi remplacer Nominatim ICI et pas partout
// Nominatim décrit très bien les RUES d'OpenStreetMap, mais très mal les
// COMMERCES : chercher « Pharmacie » à Cotonou y renvoyait trois entrées
// anonymes (« Pharmacie, Boulevard de France ») là où Google liste les
// enseignes nommées et les classe par proximité. C'est le seul endroit où
// l'écart se voyait — le géocodage inverse (coordonnées → adresse), lui, reste
// sur Nominatim : il rend un résultat équivalent, sans SKU facturé.
//
// ## Le jeton de session n'est pas un détail de facturation
// Google facture l'autocomplétion À LA SESSION quand les frappes successives
// et le Place Details final partagent un `AutocompleteSessionToken` — et à la
// REQUÊTE sinon. Une recherche d'adresse, c'est cinq à dix frappes : sans
// jeton, la même saisie coûte dix fois plus cher. Le jeton est donc créé au
// début d'une saisie et consommé par le `fetchFields` de la sélection ; après
// quoi il ne doit PLUS servir, d'où sa remise à zéro.

type PlacesLibrary = {
  AutocompleteSuggestion: typeof google.maps.places.AutocompleteSuggestion;
  AutocompleteSessionToken: typeof google.maps.places.AutocompleteSessionToken;
  Place: typeof google.maps.places.Place;
};

let sessionToken: google.maps.places.AutocompleteSessionToken | null = null;
// Le `Place` de chaque suggestion, gardé pour la résolution : il porte le
// placeId côté Google et évite de le rechercher à la sélection.
const pendingPlaces = new Map<string, google.maps.places.Place>();

/**
 * Termine la session de saisie en cours sans sélectionner une prédiction.
 *
 * Google demande un jeton neuf pour chaque nouvelle session. Cette fonction
 * est appelée quand le client vide/quitte le champ, et quand il choisit un
 * résultat qui possède déjà ses coordonnées (Text Search ou Nominatim) : aucun
 * Place Details ne viendra alors naturellement clôturer la session.
 */
export function resetPlaceSearchSession(): void {
  sessionToken = null;
  pendingPlaces.clear();
}

/** Démarre une nouvelle session de saisie (jeton neuf). */
async function ensureSessionToken(lib: PlacesLibrary) {
  if (!sessionToken) sessionToken = new lib.AutocompleteSessionToken();
  return sessionToken;
}

/**
 * Suggestions d'adresses pour l'autocomplétion du sélecteur sur carte.
 *
 * Ne renvoie PAS les coordonnées : Google ne les donne qu'au Place Details,
 * facturé à part. Elles sont résolues par `resolvePlaceSuggestion` au moment où
 * l'utilisateur choisit — une fois, au lieu d'une fois par suggestion affichée.
 *
 * Renvoie `[]` en cas d'échec (jamais `throw`) : l'appelant retombe alors sur
 * Nominatim, et la recherche continue de fonctionner sans Google.
 */
export async function searchPlaces(
  query: string,
  near?: Coordinate,
  limit = 12,
): Promise<AddressSuggestion[]> {
  const q = query.trim();
  if (q.length < 3 || !env.googleMapsApiKey) return [];

  try {
    const lib = await loadGoogleMapsLibrary<PlacesLibrary>("places");
    const token = await ensureSessionToken(lib);

    const locationBias = near
      ? {
          center: { lat: near.latitude, lng: near.longitude },
          // Biais, pas restriction : le lieu reste trouvable hors de la ville.
          radius: 50_000,
        }
      : undefined;

    // Autocomplete répond vite pendant la frappe mais retourne peu de
    // prédictions. Text Search complète les commerces, quartiers et repères.
    // On ne demande que les champs affichés ou requis par la carte.
    const [autocompleteResult, textSearchResult] = await Promise.allSettled([
      lib.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: q,
        sessionToken: token,
        language: "fr",
        region: "bj",
        ...(locationBias ? { locationBias } : {}),
      }),
      lib.Place.searchByText({
        textQuery: q,
        fields: ["id", "displayName", "formattedAddress", "location"],
        language: "fr",
        region: "BJ",
        maxResultCount: limit,
        ...(locationBias ? { locationBias } : {}),
      }),
    ]);

    // Text Search peut ne pas être activée sur un projet Google existant. Son
    // indisponibilité ne doit jamais faire disparaître les prédictions rapides
    // d'Autocomplete, déjà fonctionnelles dans l'application.
    const autocomplete =
      autocompleteResult.status === "fulfilled" ? autocompleteResult.value : { suggestions: [] };
    const textSearch =
      textSearchResult.status === "fulfilled" ? textSearchResult.value : { places: [] };

    const fromAutocomplete = autocomplete.suggestions
      .slice(0, limit)
      .map((s): AddressSuggestion | null => {
        const prediction = s.placePrediction;
        if (!prediction) return null;

        const id = prediction.placeId;
        if (!id) return null;

        pendingPlaces.set(id, prediction.toPlace());

        // `mainText` porte le nom de l'enseigne (« Pharmacie Camp Guézo »),
        // `secondaryText` la rue et la ville. Nominatim n'avait que la ligne
        // complète, d'où des libellés qui se ressemblaient tous.
        const main = prediction.mainText?.text ?? prediction.text?.text ?? "";
        const secondary = prediction.secondaryText?.text ?? "";

        return {
          id,
          placeId: id,
          label: main,
          fullName: [main, secondary].filter(Boolean).join(", "),
        } satisfies AddressSuggestion;
      })
      .filter((s): s is AddressSuggestion => s !== null);

    const fromTextSearch = textSearch.places
      .map((place): AddressSuggestion | null => {
        const id = place.id;
        const location = place.location;
        const label = place.displayName?.trim();
        if (!id || !location || !label) return null;

        const address = place.formattedAddress?.trim();
        return {
          id,
          placeId: id,
          label,
          fullName: address && !address.startsWith(label) ? `${label}, ${address}` : address ?? label,
          // Text Search fournit déjà les coordonnées : aucun Place Details
          // supplémentaire à déclencher au clic.
          coord: { latitude: location.lat(), longitude: location.lng() },
        } satisfies AddressSuggestion;
      })
      .filter((s): s is AddressSuggestion => s !== null);

    const seen = new Set<string>();
    return [...fromAutocomplete, ...fromTextSearch]
      .filter((suggestion) => {
        if (seen.has(suggestion.id)) return false;
        seen.add(suggestion.id);
        return true;
      })
      .slice(0, limit);
  } catch (e) {
    if (env.isDev) console.warn("[places] autocomplete indisponible, repli Nominatim:", e);
    return [];
  }
}

/**
 * Résout les coordonnées d'une suggestion choisie (Place Details).
 *
 * C'est l'appel facturé de la session : il la clôt, et le jeton est renouvelé
 * pour la saisie suivante.
 */
export async function resolvePlaceSuggestion(
  suggestion: AddressSuggestion,
): Promise<{ coord: Coordinate; fullName: string } | null> {
  if (suggestion.coord) {
    resetPlaceSearchSession();
    return { coord: suggestion.coord, fullName: suggestion.fullName };
  }

  const place = suggestion.placeId ? pendingPlaces.get(suggestion.placeId) : undefined;
  if (!place) {
    resetPlaceSearchSession();
    return null;
  }

  // ⚠️ PAS de `finally` ici. Il s'exécute AUSSI sur les sorties en échec, et il
  // vidait `pendingPlaces` : la liste de suggestions restait pourtant à
  // l'écran, si bien qu'un second appui sur la même ligne ne trouvait plus
  // aucun `Place` et échouait à coup sûr. Le cache n'est donc vidé que sur le
  // chemin qui l'a réellement consommé.
  try {
    await place.fetchFields({ fields: ["location", "formattedAddress", "displayName"] });
    const location = place.location;
    if (!location) {
      // Le jeton est consommé par la tentative, le cache reste utilisable.
      sessionToken = null;
      return null;
    }

    const name = place.displayName ?? suggestion.label;
    const address = place.formattedAddress ?? suggestion.fullName;

    // Résolution aboutie : la session est close et le cache n'a plus d'objet.
    // La saisie suivante repart sur un jeton neuf, sans quoi Google
    // refacturerait tout à la requête.
    resetPlaceSearchSession();

    return {
      coord: { latitude: location.lat(), longitude: location.lng() },
      // Le nom de l'enseigne d'abord : c'est ce que le client reconnaît, et
      // `formattedAddress` seul donnerait une rue sans commerce.
      fullName: address.startsWith(name) ? address : `${name}, ${address}`,
    };
  } catch (e) {
    if (env.isDev) console.warn("[places] Place Details a échoué:", e);
    sessionToken = null;
    return null;
  }
}
